/**
 * <seed-carousel> — horizontal scroll-snap carousel.
 *
 * Progressive enhancement: the track is a plain overflow-x scroller, so it
 * works with zero JS (touch, trackpad, keyboard). This element only adds the
 * arrows, the scroll progress rail and mouse-drag panning on top of it.
 *
 * Expected children (any depth):
 *   [data-carousel-track]     the scroll container
 *   [data-carousel-prev]      previous button (optional)
 *   [data-carousel-next]      next button (optional)
 *   [data-carousel-progress]  progress rail (optional, thumb is [data-carousel-thumb])
 */
class SeedCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    if (!this.track) return;

    this.prevBtn = this.querySelector('[data-carousel-prev]');
    this.nextBtn = this.querySelector('[data-carousel-next]');
    this.thumb = this.querySelector('[data-carousel-thumb]');

    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.ticking = false;

    this.onScroll = this.onScroll.bind(this);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    this.prevBtn?.addEventListener('click', () => this.step(-1));
    this.nextBtn?.addEventListener('click', () => this.step(1));

    this.initDrag();

    this.resizeObserver = new ResizeObserver(() => this.sync());
    this.resizeObserver.observe(this.track);

    this.sync();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.resizeObserver?.disconnect();
  }

  /** Distance of one "page" — a single slide plus the gap. */
  get stepSize() {
    const slide = this.track.querySelector('[data-carousel-slide]');
    if (!slide) return this.track.clientWidth;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return slide.getBoundingClientRect().width + gap;
  }

  get maxScroll() {
    return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
  }

  step(direction) {
    this.track.scrollBy({
      left: this.stepSize * direction,
      behavior: this.reduceMotion.matches ? 'auto' : 'smooth',
    });
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.sync();
      this.ticking = false;
    });
  }

  sync() {
    const max = this.maxScroll;
    // Sub-pixel scroll positions mean exact equality never lands; 1px of slack.
    const atStart = this.track.scrollLeft <= 1;
    const atEnd = this.track.scrollLeft >= max - 1;

    this.toggleAttribute('data-scrollable', max > 1);
    if (this.prevBtn) this.prevBtn.disabled = atStart;
    if (this.nextBtn) this.nextBtn.disabled = atEnd;

    if (this.thumb) {
      const visibleRatio = max > 0 ? this.track.clientWidth / this.track.scrollWidth : 1;
      const progress = max > 0 ? this.track.scrollLeft / max : 0;
      const width = Math.max(0.12, Math.min(1, visibleRatio));
      this.thumb.style.width = `${width * 100}%`;
      this.thumb.style.transform = `translateX(${(progress * (1 - width) * 100) / width}%)`;
    }
  }

  /**
   * Mouse-drag panning. Touch already pans natively, so this is mouse-only —
   * hijacking pointer events on touch would fight the browser's own scroller.
   */
  initDrag() {
    let startX = 0;
    let startScroll = 0;
    let dragging = false;
    let moved = 0;

    this.track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      dragging = true;
      moved = 0;
      startX = event.clientX;
      startScroll = this.track.scrollLeft;
      this.track.setPointerCapture(event.pointerId);
      this.setAttribute('data-dragging', '');
    });

    this.track.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      const delta = event.clientX - startX;
      moved = Math.max(moved, Math.abs(delta));
      this.track.scrollLeft = startScroll - delta;
    });

    const end = (event) => {
      if (!dragging) return;
      dragging = false;
      this.removeAttribute('data-dragging');
      if (this.track.hasPointerCapture?.(event.pointerId)) {
        this.track.releasePointerCapture(event.pointerId);
      }
      // A drag that travelled far enough must not also fire the card's link.
      if (moved > 6) {
        const swallow = (clickEvent) => {
          clickEvent.preventDefault();
          clickEvent.stopPropagation();
        };
        this.track.addEventListener('click', swallow, { capture: true, once: true });
        setTimeout(() => this.track.removeEventListener('click', swallow, { capture: true }), 0);
      }
    };

    this.track.addEventListener('pointerup', end);
    this.track.addEventListener('pointercancel', end);
    this.track.addEventListener('dragstart', (event) => {
      if (dragging) event.preventDefault();
    });
  }
}

if (!customElements.get('seed-carousel')) {
  customElements.define('seed-carousel', SeedCarousel);
}
